import { Bell, ChevronLeft, Menu, Moon, Sun, X } from 'lucide-react';

import LogoMark from '@components/layout/LogoMark';
import { useSidebarContext } from '@context/SidebarContext';
import { useToast } from '@context/ToastContext';
import { useMediaQuery } from '@hooks/useMediaQuery';
import { useTheme } from '@hooks/useTheme';
import { cn } from '@utils/cn';

import IconButton from './IconButton';
import ProfileMenu from './ProfileMenu';
import SearchBox from './SearchBox';

export default function Topbar() {
  const { collapsed, toggle, mobileOpen, openMobile, closeMobile } = useSidebarContext();
  const { isDark, toggleTheme } = useTheme();
  const isDesktop = useMediaQuery('(min-width: 1024px)');
  const toast = useToast();

  const railed = isDesktop && collapsed;

  return (
    <header className="sticky top-0 z-30 flex h-topbar shrink-0 items-center border-b border-line bg-surface pr-3 sm:pr-5">
      {/* Brand cell — matches the sidebar width on desktop, compact on mobile. */}
      <div
        className={cn(
          'flex h-full shrink-0 items-center gap-2.5 overflow-hidden px-3 transition-[width] duration-200',
          'lg:border-r lg:border-line',
          railed ? 'lg:w-sidebar-min lg:justify-center lg:px-0' : 'lg:w-sidebar-w lg:px-5',
        )}
      >
        {/* Mobile hamburger / close — toggles the off-canvas drawer. */}
        <button
          type="button"
          onClick={mobileOpen ? closeMobile : openMobile}
          aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={mobileOpen}
          className="grid h-9 w-9 shrink-0 place-items-center rounded-md text-t2 transition-colors hover:bg-surface-2 hover:text-t1 lg:hidden"
        >
          {mobileOpen ? <X size={18} /> : <Menu size={18} />}
        </button>

        <LogoMark collapsed={railed} />
      </div>

      {/* Desktop collapse toggle — a circular control straddling the sidebar's
          right edge. The conventional rail toggle; it tracks the sidebar width. */}
      <button
        type="button"
        onClick={toggle}
        aria-label={railed ? 'Expand sidebar' : 'Collapse sidebar'}
        aria-expanded={!railed}
        className={cn(
          'absolute top-1/2 z-40 hidden h-7 w-7 -translate-y-1/2 place-items-center rounded-full',
          'border border-line bg-surface text-t2 shadow-sm transition-[left,color,background-color] duration-200',
          'hover:bg-surface-2 hover:text-t1 lg:grid',
          railed
            ? 'lg:left-[calc(var(--sidebar-min)-14px)]'
            : 'lg:left-[calc(var(--sidebar-w)-14px)]',
        )}
      >
        <ChevronLeft size={15} className={cn('transition-transform', railed && 'rotate-180')} />
      </button>

      <div className="flex flex-1 items-center gap-2 px-3 sm:gap-3 sm:px-5">
        {/* Search is space-hungry — show it from md up only. */}
        <div className="hidden min-w-0 flex-1 md:block">
          <SearchBox />
        </div>
        <div className="flex-1 md:hidden" />
        <IconButton
          onClick={() => toast.info('No new notifications.')}
          aria-label="Notifications"
          hasIndicator
        >
          <Bell size={16} />
        </IconButton>
        <IconButton onClick={toggleTheme} aria-label="Toggle theme">
          {isDark ? <Sun size={16} /> : <Moon size={16} />}
        </IconButton>
        <ProfileMenu />
      </div>
    </header>
  );
}
